import {ViewedFileProgress} from './ViewedFileProgress'
import {OpenCommentsSidePanelButton} from './OpenCommentsSidePanelButton'
import {OpenAlertsPanelButton} from './OpenAlertsPanelButton'
import {ReviewMenuButton} from './ReviewMenuButton'
import {DiffViewSettings} from '@github-ui/diff-view-settings'
import type {FilesPageNotices, UserNotice, FilesRoutePayload, PageLimits} from '../../page-data/payloads/files'
import {RefreshButton} from './RefreshButton'
import {Stack, Tooltip, Button, CounterLabel} from '@primer/react'
import {DiscardBatchConfirmationDialog} from '@github-ui/diff-lines/discard-batch-confirmation-dialog'
import type {SuggestionDetails, SuggestionIdentifier} from '@github-ui/diff-lines/conversation-types'
import {ChangesSelector} from '../diff-filtering/ChangesSelector'
import {useState, useCallback} from 'react'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'
import {PullRequestStateLabel} from '../PullRequestStateLabel'
import {clsx} from 'clsx'
import styles from './PullRequestFilesToolbar.module.css'
import {PullRequestHeaderSummary} from '../PullRequestHeaderSummary'
import {useHeaderPageData} from '../../page-data/loaders/use-header-page-data'
import {useHeaderLiveUpdates} from '../../hooks/use-header-live-updates'
import type {HeaderPageData} from '../../page-data/payloads/header'
import {
  useComparisonInfo,
  useIsSingleFileMode,
  useIsSuggestionBatchPending,
  useSuggestionBatch,
  useSuggestionBatchActions,
  useAllMarkers,
  usePullRequestBasePath,
  useShowChangeGroupsPreference,
} from '../../stores/selectors'
import {useUpdateWhitespacePreferenceMutation} from '../../mutations/use-update-whitespace-preference-mutation'
import {ReviewNavigationButtonGroup} from './ReviewNavigationButtonGroup'
import {ToolbarMoreMenu} from './ToolbarMoreMenu'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ChangeGroupsPreferences} from './ChangeGroupsPreferences'
import {ApplySuggestionDialog} from '@github-ui/diff-lines/apply-suggestion-dialog'
import {useApplyBatchedSuggestionsMutation} from '../../mutations/use-apply-batched-suggestions-mutation'
import {useApplyUnifiedBatchedSuggestionsMutation} from '../../mutations/use-apply-unified-batched-suggestions-mutation'
import {getSuggestionDetails} from '../../helpers/get-suggestion-details'
import {saveActiveBatchToStorage, clearActiveBatchFromStorage} from '../../helpers/suggestion-batch-storage'
import {useUpdateShowChangeGroupsPreferenceMutation} from '../../mutations/use-update-show-change-groups-preference-mutation'
import {OpenOverviewSidePanelButton} from './OpenOverviewPanelButton'
import {useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'

export type PullRequestFilesToolbarProps = {
  changeGroupsPreferenceAvailable: boolean
  commits: FilesRoutePayload['commits']
  currentUserLogin?: string
  fileFilter: React.ReactElement
  isFileTreeExpanded?: boolean
  lastReviewOid?: string
  pageLimits: PageLimits
  pullRequest: FilesRoutePayload['pullRequest']
  repository: FilesRoutePayload['repository']
  shouldShowViewedFilesCount: boolean
  totalFilesCount: number
  treeToggleElement?: React.ReactElement
  userNotices?: Array<UserNotice<FilesPageNotices>>
}

export function LivePullRequestFilesToolbar({
  aliveChannel,
  repository,
  pullRequest: initialPullRequest,
  bannersData,
  urls,
  user,
  userNotices,
  pageTitle,
  ...pullRequestFilesToolbarProps
}: PullRequestFilesToolbarProps & HeaderPageData) {
  const {
    data: {pullRequest},
  } = useHeaderPageData()
  useHeaderPageData({aliveChannel, repository, pullRequest: initialPullRequest, bannersData, urls, user, pageTitle})
  useHeaderLiveUpdates(aliveChannel)

  const updatedPullRequest = {...initialPullRequest, ...pullRequest} as FilesRoutePayload['pullRequest']

  return (
    <PullRequestFilesToolbar
      repository={repository}
      pullRequest={updatedPullRequest}
      userNotices={userNotices}
      {...pullRequestFilesToolbarProps}
    />
  )
}

export function PullRequestFilesToolbar({
  changeGroupsPreferenceAvailable,
  commits,
  currentUserLogin,
  fileFilter,
  isFileTreeExpanded,
  lastReviewOid,
  pageLimits,
  pullRequest,
  repository,
  shouldShowViewedFilesCount,
  totalFilesCount,
  treeToggleElement,
  userNotices,
}: PullRequestFilesToolbarProps) {
  const batchSuggestedChangesEnabled = useFeatureFlag('batch_suggested_changes')
  const robustSmallScreenSupport = useFeatureFlag('prx_robust_small_screen_support')
  const {mutate: updateWhitespace} = useUpdateWhitespacePreferenceMutation()
  const {clearSuggestionBatch, removeSuggestionFromBatch} = useSuggestionBatchActions()
  const {mutate: applyBatchedSuggestions} = useApplyBatchedSuggestionsMutation(
    pullRequest.pathName,
    pullRequest.comparison.headOid,
  )
  const {mutate: applyUnifiedBatchedSuggestions} = useApplyUnifiedBatchedSuggestionsMutation(
    pullRequest.pathName,
    pullRequest.comparison.headOid,
  )
  const {mutate: updateChangeGroupPreference} = useUpdateShowChangeGroupsPreferenceMutation()
  const showChangeGroups = useShowChangeGroupsPreference()
  const {hideWhitespace, fullDiff} = useComparisonInfo()
  const [isSticky, setIsSticky] = useState(false)
  const showDivider = shouldShowViewedFilesCount || !isFileTreeExpanded
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false)

  const isSingleFileMode = useIsSingleFileMode()
  const isSuggestionBatchPending = useIsSuggestionBatchPending()
  const suggestionBatch = useSuggestionBatch()
  const markers = useAllMarkers()
  const [isCommitDialogOpen, setIsCommitDialogOpen] = useState(false)
  const pullRequestBasePath = usePullRequestBasePath()
  const comparisonInfo = useComparisonInfo()

  const [changesSelectorOpen, setChangesSelectorOpen] = useState(false)

  const suggestionDetails = getSuggestionDetails(markers, suggestionBatch.activeBatch)
  const suggestionsCount = suggestionDetails.length

  const onUpdateWhitespace = (updatedValue: boolean) => {
    updateWhitespace({hideWhitespace: updatedValue})
  }

  const sensorRef = useIntersectionObserver(entries => {
    if (entries[0]) {
      setIsSticky(!entries[0].isIntersecting)
    }
  })

  const handleRemoveSuggestion = useCallback(
    (suggestionId: SuggestionIdentifier) => {
      removeSuggestionFromBatch(suggestionId)
      saveActiveBatchToStorage(
        pullRequestBasePath,
        comparisonInfo.fullDiff.headOid,
        suggestionBatch.activeBatch.filter(
          id =>
            !(
              id.commentDatabaseId === suggestionId.commentDatabaseId &&
              id.suggestionIndex === suggestionId.suggestionIndex
            ),
        ),
      )
    },
    [removeSuggestionFromBatch, suggestionBatch, pullRequestBasePath, comparisonInfo],
  )

  const handleCommitBatch = useCallback(() => {
    setIsCommitDialogOpen(true)
  }, [])

  const handleCommitDialogClose = useCallback(() => {
    setIsCommitDialogOpen(false)
  }, [])

  const handleDiscardClick = useCallback(() => {
    setShowDiscardConfirmation(true)
  }, [])

  const handleDiscardCancel = useCallback(() => {
    setShowDiscardConfirmation(false)
  }, [])

  const handleDiscardBatch = useCallback(() => {
    clearSuggestionBatch()
    clearActiveBatchFromStorage(pullRequestBasePath, comparisonInfo.fullDiff.headOid)
    handleCommitDialogClose()
    setShowDiscardConfirmation(false)
  }, [clearSuggestionBatch, pullRequestBasePath, comparisonInfo, handleCommitDialogClose])

  const handleCommit = useCallback(
    (commitMessage: string, onError: (error: Error) => void, onCompleted: () => void) => {
      const unifiedBatchEndpointEnabled = isFeatureEnabled('copilot_code_review_batch_apply_suggestions')

      const mutationCallbacks = {
        onSuccess: () => {
          handleCommitDialogClose()
          onCompleted()
        },
        onError: (error: Error) => {
          onError(error)
        },
      }

      if (unifiedBatchEndpointEnabled) {
        applyUnifiedBatchedSuggestions({message: commitMessage}, mutationCallbacks)
        return
      }

      applyBatchedSuggestions({message: commitMessage, currentOid: fullDiff.headOid}, mutationCallbacks)
    },
    [fullDiff, applyBatchedSuggestions, applyUnifiedBatchedSuggestions, handleCommitDialogClose],
  )
  const onShowChangeGroupsChange = (newVal: boolean) => {
    updateChangeGroupPreference({showChangeGroups: newVal})
  }

  const suggestions = suggestionDetails.map(
    (detail): SuggestionDetails => ({
      commentDatabaseId: detail.commentDatabaseId,
      suggestionIndex: detail.suggestionIndex,
      path: detail.path,
      bodyHTML: detail.bodyHTML,
    }),
  )

  const sidePanelOpen = useIsDockedPanelOpen()

  return (
    <>
      <div ref={sensorRef} className={styles.stickyHeaderActivationThreshold} />
      <Stack
        as="section"
        direction="horizontal"
        justify="space-between"
        gap="condensed"
        align="center"
        className={clsx(stickyStyles.stickyHeader, styles.toolbar, isSticky && styles['is-stuck'])}
        data-side-panel-open={sidePanelOpen}
      >
        <h2 className="sr-only">Pull Request Toolbar</h2>

        <Stack direction="horizontal" gap="condensed" align="center" className="min-width-0">
          <div>{treeToggleElement}</div>
          <div className={styles['show-when-stuck']}>
            <PullRequestStateLabel state={pullRequest.state} />
          </div>
          <div
            className={clsx(
              styles['hide-when-stuck-large'],
              robustSmallScreenSupport && styles['hide-commit-selector-on-small'],
            )}
          >
            <ChangesSelector
              commits={commits}
              lastReviewOid={lastReviewOid}
              ownerLogin={repository.ownerLogin}
              pullRequestNumber={pullRequest.number}
              repositoryName={repository.name}
            />
          </div>
          <Stack direction="vertical" gap="none" className={clsx(styles['show-when-stuck'], 'min-width-0')}>
            <div className={clsx('d-flex mb-n1', styles['pr-title-details'])}>
              <Tooltip type="label" direction="s" text={pullRequest.title}>
                <a href="#top" className="d-flex overflow-hidden fgColor-default">
                  <bdi className={clsx('f5 text-bold overflow-hidden no-wrap', styles['pr-sticky-title'])}>
                    {pullRequest.title}
                  </bdi>
                </a>
              </Tooltip>
              <span className="f5 text-normal pl-2 fgColor-muted d-inline">#{pullRequest.number}</span>
            </div>

            <div
              className={clsx(
                'f6 text-normal d-flex flex-items-center ml-n2',
                robustSmallScreenSupport && styles['hide-commit-selector-on-small'],
              )}
            >
              <ChangesSelector
                commits={commits}
                lastReviewOid={lastReviewOid}
                ownerLogin={repository.ownerLogin}
                pullRequestNumber={pullRequest.number}
                repositoryName={repository.name}
                variant="condensed"
                open={changesSelectorOpen}
                onOpenChange={setChangesSelectorOpen}
              />
              <div className={clsx('d-none d-lg-flex flex-items-center min-width-0', styles['pr-title-details'])}>
                <div className="border-left mx-1 pr-1 d-block" style={{width: '1px', height: '16px'}} />
                <PullRequestHeaderSummary
                  author={pullRequest.author}
                  baseBranch={pullRequest.baseBranch}
                  baseRepositoryOwnerLogin={repository.ownerLogin}
                  baseRepositoryName={repository.name}
                  commitsCount={pullRequest.commitsCount}
                  headBranch={pullRequest.headBranch}
                  headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                  headRepositoryName={pullRequest.headRepositoryName}
                  isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                  mergedBy={pullRequest.mergedBy}
                  mergedByName={pullRequest.mergedByName}
                  state={pullRequest.state}
                  pullRequestId={pullRequest.id}
                  pullRequestNumber={pullRequest.number}
                  repositoryId={repository.id}
                />
              </div>
            </div>
          </Stack>
        </Stack>
        <Stack direction="horizontal" align="center">
          <RefreshButton aliveChannel={pullRequest.aliveChannel} />

          <div className={styles['file-controls']}>
            {shouldShowViewedFilesCount && <ViewedFileProgress totalFilesCount={totalFilesCount} />}

            {!isFileTreeExpanded && fileFilter}
            {isSingleFileMode && totalFilesCount > 1 && (
              <div className={styles['file-controls-navigation']}>
                <ReviewNavigationButtonGroup userNotices={userNotices} />
              </div>
            )}
          </div>
          {showDivider && <div className={styles['file-controls-divider']} data-testid="file-controls-divider" />}
          {isSuggestionBatchPending && batchSuggestedChangesEnabled ? (
            <>
              <Button variant="danger" size="small" onClick={handleDiscardClick}>
                Discard batch
              </Button>
              <Button variant="primary" size="small" onClick={handleCommitBatch}>
                Commit suggestions <CounterLabel className="ml-1 color-fg-inherit">{suggestionsCount}</CounterLabel>
              </Button>
            </>
          ) : (
            <>
              <OpenOverviewSidePanelButton aliveChannel={pullRequest.aliveChannel} />
              <div className="d-none d-sm-flex">
                <OpenCommentsSidePanelButton
                  pageLimits={pageLimits}
                  pullRequest={pullRequest}
                  isFileTreeExpanded={isFileTreeExpanded}
                />
              </div>
              <OpenAlertsPanelButton
                className="d-none d-sm-flex"
                pageLimits={pageLimits}
                isFileTreeExpanded={isFileTreeExpanded}
              />

              {currentUserLogin && (
                <ReviewMenuButton
                  currentUserLogin={currentUserLogin}
                  pullRequest={pullRequest}
                  repository={repository}
                />
              )}
              <div className="d-none d-sm-flex">
                <DiffViewSettings
                  invisible={false}
                  reloadOnWhitespaceChange
                  onUpdateWhitespace={onUpdateWhitespace}
                  defaultHideWhitespace={hideWhitespace}
                  small
                  userNotices={userNotices}
                  additionalPreferences={
                    <ChangeGroupsPreferences
                      changeGroupsPreferenceAvailable={changeGroupsPreferenceAvailable}
                      showChangeGroups={showChangeGroups}
                      onShowChangeGroupsChange={onShowChangeGroupsChange}
                    />
                  }
                />
              </div>

              {/* More options menu for small screens */}
              <div className="d-flex d-sm-none">
                <ToolbarMoreMenu
                  pageLimits={pageLimits}
                  pullRequest={pullRequest}
                  hideWhitespace={hideWhitespace}
                  onUpdateWhitespace={onUpdateWhitespace}
                  showCommitSelector={() => setChangesSelectorOpen(true)}
                  commits={commits}
                  additionalPreferences={
                    <ChangeGroupsPreferences
                      changeGroupsPreferenceAvailable={changeGroupsPreferenceAvailable}
                      showChangeGroups={showChangeGroups}
                      onShowChangeGroupsChange={onShowChangeGroupsChange}
                    />
                  }
                />
              </div>
            </>
          )}
        </Stack>
      </Stack>
      {isCommitDialogOpen && suggestions.length > 0 && (
        <ApplySuggestionDialog
          authorLogins={[]}
          batchSize={suggestionBatch.activeBatch.length}
          onClose={handleCommitDialogClose}
          onCommit={handleCommit}
          defaultCommitMessage="Apply suggestions from code review"
          suggestions={suggestions}
          onRemoveSuggestion={handleRemoveSuggestion}
          onDiscardBatch={handleDiscardBatch}
        />
      )}
      <DiscardBatchConfirmationDialog
        isOpen={showDiscardConfirmation}
        onConfirm={handleDiscardBatch}
        onCancel={handleDiscardCancel}
        suggestionsCount={suggestionsCount}
      />
    </>
  )
}
